import React, { useCallback } from 'react';
import {
    Accordion,
    FlexyButton,
    UploaderProvider,
    useModal,
} from '@europrocurement/flexy-components';
import type {
    Acompte,
    Collection,
    Reglement,
} from '@europrocurement/l2d-modules/providers/CollectionBillingProvider';
import { Box, Card, PaletteColor, Typography, useTheme } from '@mui/material';
import { addIcon } from '@europrocurement/l2d-icons';
import type { DocumentGed } from '@europrocurement/l2d-modules/modules/Dossiers/hooks';
import {
    DocumentPreviewProvider,
    useCurrentDossierCtx,
} from '@europrocurement/l2d-modules/modules/Dossiers/providers';
import CollectionProvision from '../../../CollectionProvision/CollectionProvision';
import ProvisionAdd from '../../../ProvisionAdd/ProvisionAdd';
import DocumentsModalContent from '../DocumentsModalContent/DocumentsModalContent';
import useDossierFacturationActions from '../../hooks/useDossierFacturationActions';

type EncaissementsSectionProps = {
    collections: Collection[];
    totalAcomptes: number;
    totalReglements: number;
    reglements: Array<Reglement>;
    acomptes: Array<Acompte>;
    reglementsDocs: DocumentGed[];
    setReglementsDocs: React.Dispatch<React.SetStateAction<DocumentGed[]>>;
};

const EncaissementsSection: React.FunctionComponent<EncaissementsSectionProps> = function ({
    collections,
    totalAcomptes,
    totalReglements,
    reglements,
    acomptes,
    reglementsDocs,
    setReglementsDocs,
}: EncaissementsSectionProps) {
    const { modalActions } = useModal();
    const { palette } = useTheme();
    const { dossier } = useCurrentDossierCtx();
    const { createAcompte } = useDossierFacturationActions();

    const handleCreateAcompte = async (acompte: Acompte, files?: File[]) => {
        const collection = collections[0];

        if (!collection) {
            return;
        }

        modalActions.close();
        const result = await createAcompte(collection, acompte, files, dossier);

        if (result.uploadedDocs.length > 0) {
            setReglementsDocs((prev) => [...prev, ...result.uploadedDocs]);
        }
    };

    const handleAddAttachmentFile = useCallback(
        (reglementId?: number) => {
            if (dossier && reglementId) {
                modalActions.call(
                    <DocumentPreviewProvider>
                        <DocumentsModalContent
                            dossier={dossier}
                            reglementsDocGed={reglementsDocs}
                            reglementId={reglementId}
                            setReglementsDocs={setReglementsDocs}
                        />
                    </DocumentPreviewProvider>,
                    true,
                    true,
                );
            }
        },
        [modalActions, reglementsDocs, dossier, setReglementsDocs],
    );

    return (
        <Card
            id="reglements"
            sx={{ boxShadow: 'none', padding: 0, mx: 0 }}
        >
            <Accordion
                sx={{
                    boxShadow: 'none',
                }}
                title="Règlements"
                titlesSx={{
                    fontWeight: '600',
                    boxShadow: 'none',
                }}
                headerSx={{ boxShadow: 'none' }}
                expanded
                expandColor={palette.text.primary as unknown as PaletteColor}
                expandLabel={
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: '10px',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                justifyContent: 'space-evenly',
                            }}
                        >
                            <Typography
                                variant="l2dh7"
                                sx={{ marginRight: '10px', fontSize: '0.875rem' }}
                            >
                                Total réglé :
                            </Typography>
                            <Typography
                                variant="l2dh7"
                                sx={{ color: 'text.primary' }}
                            >
                                {` ${totalAcomptes + totalReglements} €`}
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                justifyContent: 'space-evenly',
                            }}
                        >
                            <Typography
                                variant="l2dh7"
                                sx={{ marginRight: '10px', fontSize: '0.875rem' }}
                            >
                                Crédit restant :
                            </Typography>
                            <Typography
                                variant="l2dh7"
                                sx={{ color: 'text.secondary' }}
                            >
                                {` ${totalAcomptes}
                                    €`}
                            </Typography>
                        </Box>
                    </Box>
                }
                footerActions={
                    collections.length === 1
                        ? [
                              <Box
                                  sx={{
                                      display: 'flex',
                                      flexDirection: 'row',
                                      justifyContent: 'end',
                                      alignItems: 'center',
                                      gap: '10px',
                                  }}
                              >
                                  <FlexyButton
                                      variant="outlined"
                                      onClick={() => {
                                          modalActions.call(
                                              <UploaderProvider>
                                                  <ProvisionAdd
                                                      onAddProvision={handleCreateAcompte}
                                                      collectionId={collections[0].id as number}
                                                      sx={{ maxWidth: '600px' }}
                                                  />
                                              </UploaderProvider>,
                                              true,
                                              false,
                                          );
                                      }}
                                      icon={addIcon}
                                      label="Ajouter un règlement"
                                  />
                              </Box>,
                          ]
                        : []
                }
            >
                <CollectionProvision
                    acomptes={[...acomptes]}
                    reglements={[...reglements]}
                    reglementsDocs={reglementsDocs}
                    handleAddAttachmentFile={handleAddAttachmentFile}
                />
            </Accordion>
        </Card>
    );
};

export default EncaissementsSection;
